import { useMemo } from 'react';

export type AppMode = 'admin' | 'super-admin' | 'partner';

interface AppModeConfig {
  mode: AppMode;
  isPartnerDomain: boolean;
  isSuperAdminDomain: boolean;
  isAdminDomain: boolean;
}

/**
 * Get app mode synchronously (for use outside React components).
 *
 * Domains:
 * - partner.insolitum.ai → Partner Portal
 * - super-admin.insolitum.ai → Super Admin Panel
 * - * (everything else) → Tenant Admin Panel
 */
export function getAppMode(): AppModeConfig {
  const hostname = window.location.hostname;

  const isPartnerDomain =
    hostname === 'partner.insolitum.ai' ||
    hostname.startsWith('partner.') ||
    hostname === 'localhost-partner'; // For local development

  const isSuperAdminDomain =
    hostname === 'super-admin.insolitum.ai' ||
    hostname.startsWith('super-admin.') ||
    hostname === 'localhost-super-admin'; // For local development

  let mode: AppMode = 'admin';
  if (isPartnerDomain) {
    mode = 'partner';
  } else if (isSuperAdminDomain) {
    mode = 'super-admin';
  }

  return {
    mode,
    isPartnerDomain,
    isSuperAdminDomain,
    isAdminDomain: !isPartnerDomain && !isSuperAdminDomain,
  };
}

/** Hook wrapper — the hostname never changes within a session. */
export function useAppMode(): AppModeConfig {
  return useMemo(getAppMode, []);
}
